import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../../styles/AdminTopNavBar.css";
import { adminReqConfig, api } from "../../include/api";
import { getAdmin } from "../../include/function";

const NotificationItem = ({ notification, onMarkAsRead }) => {
  const handleClick = async () => {
    if (!notification.url) return;

    if (notification.readStatus === "unread") {
      await onMarkAsRead(notification.id);
    }

    window.location.href = notification.url.trim();
  };

  return (
    <div
      onClick={handleClick}
      style={{
        padding: "12px",
        borderBottom: "1px solid #eee",
        cursor: "pointer",
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        backgroundColor:
          notification.readStatus === "unread" ? "#f8f9ff" : "#fff",
        fontWeight: notification.readStatus === "unread" ? "500" : "normal",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f0f4ff")}
      onMouseLeave={(e) =>
        (e.currentTarget.style.backgroundColor =
          notification.readStatus === "unread" ? "#f8f9ff" : "#fff")
      }
    >
      <div
        style={{
          width: "8px",
          height: "8px",
          borderRadius: "50%",
          backgroundColor:
            notification.readStatus === "unread" ? "#007bff" : "#ccc",
          marginTop: "8px",
        }}
      ></div>
      <div style={{ flex: 1 }}>
        <div>{notification.message || "No message"}</div>
        <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
          {new Date(notification.createTimestamp).toLocaleString()}
        </div>
      </div>
    </div>
  );
};

const AdminTopNavBar = ({
  toggleSidebar,
  isSidebarMinimized,
  homeLink,
  title,
}) => {
  const navigate = useNavigate();
  const user = getAdmin();

  // Permission helper: check if ANY of view/add/edit/delete is "true"
  const hasAnyPermission = (pageUrl) => {
    if (!user?.roleProperties) return false;
    const perm = user.roleProperties.find((rp) => rp.pageUrl === pageUrl);
    if (!perm) return false;
    return (
      perm.view === "true" ||
      perm.add === "true" ||
      perm.edit === "true" ||
      perm.delete === "true"
    );
  };

  // ✅ CORRECTED: /admin/sales/orders (not "sages")
  const canViewOrders = hasAnyPermission("/admin/sales/orders");
  const canViewCustomers = hasAnyPermission("/admin/customer/customerList"); // for "User" tab
  const canViewDocuments = hasAnyPermission("/admin/document/documentVerify");

  // Notification states
  const [rawNotifyData, setRawNotifyData] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("All");
  const [isNotificationDropdownOpen, setIsNotificationDropdownOpen] =
    useState(false);
  const notificationRef = useRef(null);

  const hasNotifications = rawNotifyData.length > 0;
  const unreadCount = rawNotifyData.filter(
    (n) => n.readStatus === "unread",
  ).length;
  const badgeText = unreadCount > 99 ? "99+" : unreadCount.toString();

  // Search states
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false);

  // Data for delivery search filtering
  const [allProducts, setAllProducts] = useState([]);
  const [allBrands, setAllBrands] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [allZones, setAllZones] = useState([]);

  // Fetch master data
  useEffect(() => {
    api
      .get("/product/", adminReqConfig())
      .then((res) => setAllProducts(res.data.data || []));
    api
      .get("/brand/", adminReqConfig())
      .then((res) => setAllBrands(res.data.data || []));
    api
      .get("/category/", adminReqConfig())
      .then((res) => setAllCategories(res.data.data || []));
    api
      .get("/zone/", adminReqConfig())
      .then((res) => setAllZones(res.data.data || []));
  }, []);

  // Close notification dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setIsNotificationDropdownOpen(false);
      }
    };

    if (isNotificationDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isNotificationDropdownOpen]);

  // ✅ FETCH AND FILTER NOTIFICATIONS BASED ON PERMISSIONS
  const getNotification = () => {
    if (!user?.id) {
      setRawNotifyData([]);
      setFetchLoading(false);
      return;
    }

    setFetchLoading(true);
    const url = `/admin_notification_status/?flag=getuserAllNotification&userId=${user.id}`;

    api
      .get(url, adminReqConfig())
      .then((response) => {
        if (response.status === 200 && response.data?.data) {
          const allNotifications = Array.isArray(response.data.data)
            ? response.data.data
            : [];

          // ✅ FILTER OUT UNPERMITTED NOTIFICATIONS
          const permittedNotifications = allNotifications.filter(
            (notification) => {
              if (notification.type === "order") return canViewOrders;
              if (notification.type === "document") return canViewDocuments;
              if (notification.type === "user") return canViewCustomers;
              return true; // allow other types (e.g., system alerts)
            },
          );

          setRawNotifyData(permittedNotifications);
        } else {
          setRawNotifyData([]);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch notifications:", error);
        setRawNotifyData([]);
      })
      .finally(() => setFetchLoading(false));
  };

  const markAsRead = async (notificationId) => {
    setRawNotifyData((prev) =>
      prev.map((n) =>
        n.id === notificationId ? { ...n, readStatus: "read" } : n,
      ),
    );

    try {
      const payload = {
        notificationId: notificationId,
        userId: user?.id,
      };

      await api.post("/admin_notification_status/", payload, adminReqConfig());
    } catch (err) {
      console.warn("Failed to mark notification as read:", err);
      setRawNotifyData((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, readStatus: "unread" } : n,
        ),
      );
    }
  };

  // ✅ Now safe: rawNotifyData is already filtered
  const getFilteredNotifications = () => {
    if (activeTab === "All") return rawNotifyData;
    if (activeTab === "Order")
      return rawNotifyData.filter((n) => n.type === "order");
    if (activeTab === "Document")
      return rawNotifyData.filter((n) => n.type === "document");
    if (activeTab === "User")
      return rawNotifyData.filter((n) => n.type === "user");
    return rawNotifyData;
  };

  const filteredNotifications = getFilteredNotifications();

  useEffect(() => {
    getNotification();
  }, []);

  useEffect(() => {
    if (!searchValue.trim()) {
      setSearchResults([]);
      setIsSearchDropdownOpen(false);
      return;
    }

    const timer = setTimeout(() => {
      const query = encodeURIComponent(searchValue.trim());

      // 🔥 Single API call to unified search endpoint
      api
        .get(`/search/?q=${query}`, adminReqConfig())
        .then((response) => {
          if (response.status === 200 && Array.isArray(response.data?.data)) {
            const results = response.data.data;

            // Group results by module for display
            const grouped = {};
            results.forEach((item) => {
              // Format module name for display (e.g., "report_sales" → "REPORT SALES")
              const moduleName =
                item.module?.toUpperCase().replace(/_/g, " ") || "OTHER";

              if (!grouped[moduleName]) {
                grouped[moduleName] = [];
              }
              grouped[moduleName].push(item);
            });

            // Convert to array format for rendering
            const formattedResults = Object.entries(grouped).map(
              ([type, items]) => ({
                type,
                items: items.map((item) => ({
                  id: `${item.module}-${item.keyword}-${item.url}`, // unique composite key
                  name: item.keyword,
                  url: item.url,
                  module: item.module,
                })),
              }),
            );

            setSearchResults(formattedResults);
            setIsSearchDropdownOpen(formattedResults.length > 0);
          } else {
            setSearchResults([]);
            setIsSearchDropdownOpen(false);
          }
        })
        .catch((error) => {
          console.error("Search failed:", error);
          setSearchResults([]);
          setIsSearchDropdownOpen(false);
        });
    }, 300);

    return () => clearTimeout(timer);
  }, [searchValue]);
  const handleSearch = (e) => {
    e.preventDefault();
    navigate(
      `/admin/notification/notificationList?q=${encodeURIComponent(searchValue.trim())}`,
    );
  };

  // ✅ Now uses direct URL from API response
  const handleSearchItemClick = (item) => {
    if (item?.url) {
      // Direct navigation using URL from API
      navigate(item.url);
    }
    setSearchValue("");
    setIsSearchDropdownOpen(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("admin");
    window.location.href = "/admin/login";
  };

  // ✅ Determine if bell should be shown at all
  const canViewAnyNotification =
    canViewOrders || canViewCustomers || canViewDocuments;

  return (
    <>
      <nav
        className="main-header navbar navbar-expand navbar-blue navbar-light d-flex justify-content-between align-items-center"
        style={{
          position: "fixed",
          top: 0,
          width: "100%",
          marginLeft: isSidebarMinimized ? "80px" : "250px",
          height: "75px",
          zIndex: 1030,
          backgroundColor: "#F1F4F9",
          padding: "0 15px",
        }}
      >
        {/* Left Section */}
        <div className="d-flex align-items-center">
          <ul className="navbar-nav me-3">
            <li className="nav-item">
              <Link
                className="nav-link"
                to="#"
                role="button"
                onClick={toggleSidebar}
              >
                <i
                  className="fas fa-bars text-dark fw-bold"
                  style={{ fontSize: "20px" }}
                ></i>
              </Link>
            </li>
          </ul>
          <h6 className="ps-16 pt-12">{title}</h6>
        </div>

        {/* Right Section */}
        <div
          className="d-flex align-items-center justify-content-end"
          style={{ flex: 1, marginRight: "250px" }}
        >
          <ul className="navbar-nav gap-8 d-flex align-items-center">
            {/* Search bar */}
            <li className="nav-item" style={{ position: "relative" }}>
              <form className="d-flex" role="search" onSubmit={handleSearch}>
                <div style={{ position: "relative", width: "500px" }}>
                  <i
                    className="fa fa-search"
                    style={{
                      position: "absolute",
                      left: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "gray",
                      pointerEvents: "none",
                      zIndex: 2,
                    }}
                  ></i>

                  <input
                    className="form-control me-5"
                    type="search"
                    placeholder="Search ..."
                    aria-label="Search"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onFocus={() =>
                      searchValue.trim() && setIsSearchDropdownOpen(true)
                    }
                    onBlur={() =>
                      setTimeout(() => setIsSearchDropdownOpen(false), 200)
                    }
                    style={{
                      width: "100%",
                      backgroundColor: "#E7EAF1",
                      paddingLeft: "36px",
                      color: "gray",
                    }}
                  />
                  {isSearchDropdownOpen && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        backgroundColor: "#fff",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                        maxHeight: "300px",
                        overflowY: "auto",
                        zIndex: 1000,
                        marginTop: "5px",
                      }}
                    >
                      {searchResults.length > 0 ? (
                        searchResults.map((section, index) => (
                          <div key={index}>
                            <div
                              style={{
                                padding: "8px 12px",
                                fontWeight: "bold",
                                backgroundColor: "#f8f9fa",
                                borderBottom: "1px solid #dee2e6",
                              }}
                            >
                              {section.type}
                            </div>
                            {section.items.map((item) => (
                              // section?.type == 'category' ?
                              <div
                                key={item.id}
                                onMouseDown={() => handleSearchItemClick(item)}
                                style={{
                                  padding: "8px 12px",
                                  cursor: "pointer",
                                  borderBottom: "1px solid #eee",
                                }}
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.backgroundColor =
                                    "#f0f0f0")
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.backgroundColor =
                                    "#fff")
                                }
                              >
                                <div style={{ fontWeight: "500" }}>
                                  {item.name || item.keyword || "N/A"}
                                </div>
                                {/* Optional: Show module path as hint */}
                                <div
                                  style={{ fontSize: "11px", color: "#888" }}
                                >
                                  {item.url}
                                </div>
                              </div>
                            ))}
                          </div>
                        ))
                      ) : (
                        <div
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            color: "#666",
                          }}
                        >
                          No results found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </form>
            </li>

            {/* ✅ Notification bell — ONLY shown if user can view at least one notification type */}
            {canViewAnyNotification && (
              <li className="nav-item" style={{ position: "relative" }}>
                <div
                  ref={notificationRef}
                  style={{ position: "relative", display: "inline-block" }}
                >
                  <div
                    className="me-8 mt-2 ms-16"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setIsNotificationDropdownOpen(
                        !isNotificationDropdownOpen,
                      );
                      if (!isNotificationDropdownOpen) {
                        getNotification();
                      }
                    }}
                  >
                    <img
                      src="/assets/images/Notification.png"
                      alt="notification"
                      style={{ width: "30px", height: "30px" }}
                    />
                    {hasNotifications && unreadCount > 0 && (
                      <span
                        style={{
                          position: "absolute",
                          top: "-8px",
                          right: "-8px",
                          backgroundColor: "red",
                          color: "white",
                          borderRadius: "50%",
                          padding: badgeText.length === 1 ? "4px" : "3px 5px",
                          fontSize: "11px",
                          fontWeight: "bold",
                          minWidth: badgeText.length === 1 ? "18px" : "22px",
                          height: badgeText.length === 1 ? "18px" : "auto",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          lineHeight: 1,
                        }}
                      >
                        {badgeText}
                      </span>
                    )}
                  </div>

                  {isNotificationDropdownOpen && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        right: 0,
                        backgroundColor: "#fff",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                        width: "400px",
                        maxHeight: "400px",
                        overflowY: "auto",
                        zIndex: 1000,
                        marginTop: "5px",
                        padding: "12px",
                      }}
                    >
                      {fetchLoading ? (
                        <div style={{ padding: "12px", textAlign: "center" }}>
                          Loading...
                        </div>
                      ) : (
                        <>
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              marginBottom: "12px",
                              borderBottom: "1px solid #eee",
                              paddingBottom: "8px",
                            }}
                          >
                            {(() => {
                              const tabs = ["All"];
                              if (canViewOrders) tabs.push("Order");
                              if (canViewDocuments) tabs.push("Document");
                              if (canViewCustomers) tabs.push("User");
                              return tabs;
                            })().map((tab) => (
                              <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                style={{
                                  padding: "6px 12px",
                                  borderRadius: "20px",
                                  border: "none",
                                  background:
                                    activeTab === tab
                                      ? "#007bff"
                                      : "rgba(0,0,0,0.05)",
                                  color: activeTab === tab ? "white" : "black",
                                  cursor: "pointer",
                                  fontWeight:
                                    activeTab === tab ? "bold" : "normal",
                                  fontSize: "14px",
                                }}
                              >
                                {tab}
                              </button>
                            ))}
                          </div>

                          {(() => {
                            const unread = filteredNotifications.filter(
                              (n) => n.readStatus === "unread",
                            );
                            const read = filteredNotifications.filter(
                              (n) => n.readStatus === "read",
                            );

                            return (
                              <>
                                {unread.length > 0 && (
                                  <>
                                    <div
                                      style={{
                                        fontWeight: "bold",
                                        color: "#007bff",
                                        padding: "8px 0",
                                        fontSize: "13px",
                                        textTransform: "uppercase",
                                        borderBottom: "1px solid #eee",
                                        marginBottom: "8px",
                                      }}
                                    >
                                      Unread
                                    </div>
                                    {unread.map((notification) => (
                                      <NotificationItem
                                        key={notification.id}
                                        notification={notification}
                                        onMarkAsRead={markAsRead}
                                      />
                                    ))}
                                  </>
                                )}

                                {read.length > 0 && (
                                  <>
                                    <div
                                      style={{
                                        fontWeight: "bold",
                                        color: "#6c757d",
                                        padding: "8px 0",
                                        fontSize: "13px",
                                        textTransform: "uppercase",
                                        borderBottom: "1px solid #eee",
                                        marginTop:
                                          unread.length > 0 ? "16px" : "0",
                                        marginBottom: "8px",
                                      }}
                                    >
                                      Read
                                    </div>
                                    {read.map((notification) => (
                                      <NotificationItem
                                        key={notification.id}
                                        notification={notification}
                                        onMarkAsRead={markAsRead}
                                      />
                                    ))}
                                  </>
                                )}

                                {unread.length === 0 && read.length === 0 && (
                                  <div
                                    style={{
                                      padding: "12px",
                                      textAlign: "center",
                                      color: "#666",
                                    }}
                                  >
                                    No notifications
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </li>
            )}

            {/* Profile dropdown */}
            <li className="nav-item dropdown" style={{ position: "relative" }}>
              <a
                className="nav-link dropdown-toggle d-flex align-items-center"
                href="#"
                role="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <div
                  style={{
                    width: "35px",
                    height: "35px",
                    borderRadius: "50%",
                    backgroundColor: "#81A4D5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "16px",
                  }}
                >
                  {(user?.name?.trim() &&
                    user.name.trim().charAt(0).toUpperCase()) ||
                    ""}
                </div>
              </a>
              <ul className="dropdown-menu dropdown-menu-end">
                <li>
                  <h6 className="text-capitalize pt-8 justify-content-center d-flex fs-5">
                    {user?.name.trim()}
                  </h6>
                </li>
                <li>
                  <button className="dropdown-item" onClick={handleLogout}>
                    Logout
                  </button>
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </nav>
    </>
  );
};

export default AdminTopNavBar;
